<template>
  <div class="animated">
    <v-client-table
      :columns="columns"
      :data="data"
      :options="options"
      :theme="theme"
      id="analyte-result-table"
      class="w-100"
    >
      <span v-if="showColumn('status')" slot="status" slot-scope="{ row }">
        <b-badge v-if="row.status === 'Pending'" variant="warning" pill
          >Pending</b-badge
        >
        <b-badge v-if="row.status === 'Declined'" variant="danger" pill
          >Declined</b-badge
        >
        <b-badge v-if="row.status === 'Approved'" variant="success" pill
          >Approved</b-badge
        >
      </span>
      <span
        v-if="showColumn('actions')"
        slot="actions"
        slot-scope="{ row, index }"
      >
        <b-button
          v-if="row.result_id && row.status === 'Pending'"
          variant="danger"
          class="px-1 py-0 mr-1"
          @click="declineResult(row, index)"
        >
          <i class="fa fa-times" />
        </b-button>
        <b-button
          v-if="row.result_id && row.status === 'Pending'"
          variant="success"
          class="px-1 py-0"
          @click="approveResult(row, index)"
        >
          <i class="fa fa-check" />
        </b-button>
      </span>
    </v-client-table>
  </div>
</template>

<script>
import moment from "moment";
import Vue from "vue";
import { ClientTable, Event } from "vue-tables-2";
import ToastNotifications from "@/components/shared/mixins/ToastNotifications";
import AnalyteCollection from "@/collections/lab/AnalyteCollection";
import AnalyteResultCollection from "@/collections/lab/AnalyteResultCollection";
import AnalyteResultStatusCollection from "@/collections/lab/AnalyteResultStatusCollection";
import AnalyteResultStatusLogModel from "@/models/lab/AnalyteResultStatusLogModel";
import SampleModel from "@/models/lab/SampleModel";

Vue.use(ClientTable);

export default {
  name: "AnalyteResultTable",
  mixins: [ToastNotifications],
  components: {
    ClientTable,
    Event
  },
  props: {
    sample: {
      type: SampleModel,
      default: () => null
    },
    analytes: {
      type: AnalyteCollection,
      default: () => new AnalyteCollection()
    },
    results: {
      type: AnalyteResultCollection,
      default: () => new AnalyteResultCollection()
    },
    show_columns: {
      type: Array,
      default: () => [
        "analyte",
        "instrument",
        "result",
        "status",
        "created_by",
        "evaluated_by",
        "actions"
      ]
    },
    show_actions: {
      type: Array,
      default: () => ["edit", "decline", "approve"]
    },
    filterable: {
      type: Array,
      default: () => ["searchable"]
    },
    sortable: {
      type: Array,
      default: () => []
    },
    trigger_approval_all: {
      type: Boolean,
      default: () => false
    }
  },
  data() {
    return {
      sample_id: this.$route.params.sample_id || null,
      columns: this.show_columns,
      data: [],
      options: {
        headings: {},
        sortable: this.sortable,
        filterable: this.filterable,
        sortIcon: {
          base: "fa",
          up: "fa-sort-asc",
          down: "fa-sort-desc",
          is: "fa-sort"
        },
        pagination: {
          chunk: 5,
          edge: false,
          nav: "scroll"
        }
      },
      useVuex: false,
      theme: "bootstrap4",
      template: "default"
    };
  },
  methods: {
    showColumn(key) {
      return this.show_columns.indexOf(key) !== -1;
    },
    declineResult(row, index) {
      return this.createStatusLog("Declined", row, index);
    },
    approveResult(row, index) {
      return this.createStatusLog("Approved", row, index);
    },
    createStatusLog(status_name, row, index) {
      let result_id = row.result_id;

      return new Promise((resolve, reject) => {
        let statuses = new AnalyteResultStatusCollection();
        statuses.getFilter().filterEquals("status", status_name);
        statuses.fetch().then(
          () => {
            let status = statuses.first();

            if (status) {
              let log = new AnalyteResultStatusLogModel({
                analyte_result_id: result_id,
                analyte_result_status_id: status.id
              });
              log.save().then(() => {
                this.quickSuccessToast(
                  "Status Update Success",
                  "Result status was updated"
                );
                row.status = log.analyte_result_status.status;
                this.data.splice(index - 1, 1, row);
                return resolve();
              });
            } else {
              this.quickDangerToast(
                "Status Update Failed",
                "Result status could not be updated"
              );
              return reject();
            }
          },
          () => {
            this.quickDangerToast(
              "Status Update Failed",
              "Result status could not be updated"
            );
            return reject();
          }
        );
      });
    }
  },
  watch: {
    trigger_approval_all() {
      if (this.trigger_approval_all) {
        this.data.forEach((row, index) => {
          this.approveResult(row, index);
        });
      }
    }
  },
  mounted() {
    if (!this.sample_id) {
      return;
    }

    const rows = [];

    if (this.analytes.models.length) {
      this.analytes.each(analyte => {
        let analyte_result = null;

        this.results.each(result => {
          if (analyte.id === result.analyte_id) {
            analyte_result = result;
          }
        });

        const i_name =
          analyte_result && analyte_result.instrument_instance
            ? analyte_result.instrument_instance.name
            : null;
        const unit =
          analyte_result && analyte_result.unit_of_measure
            ? analyte_result.unit_of_measure.abbrev
            : null;
        const r_name = analyte_result
          ? [
              analyte_result.formatted_result || null,
              unit || null,
              analyte_result.raw_result
                ? `(${analyte_result.raw_result}${unit ? unit : null})`
                : null
            ]
              .filter(j => j)
              .join("")
          : null;
        let created_by = [
          analyte_result &&
          analyte_result.created_by_user &&
          analyte_result.created_by_user.name
            ? analyte_result.created_by_user.name
            : null,
          analyte_result && analyte_result.created_at
            ? `(${moment(analyte_result.created_at).format("ll LT")})`
            : null
        ]
          .filter(l => !!l)
          .join(" ");
        let updated_by =
          analyte_result && analyte_result.updated_by_user
            ? [
                analyte_result &&
                analyte_result.updated_by_user &&
                analyte_result.updated_by_user.name
                  ? analyte_result.updated_by_user.name
                  : null,
                analyte_result && analyte_result.updated_at
                  ? `(${moment(analyte_result.updated_at).format("ll LT")})`
                  : null
              ]
                .filter(l => !!l)
                .join(" ")
            : null;
        let status =
          analyte_result && analyte_result.current_analyte_result_status_log
            ? analyte_result.current_analyte_result_status_log
                .analyte_result_status.status
            : null;
        rows.push({
          sample_id: this.sample_id,
          analyte_id: analyte.id,
          analyte: [
            analyte.abbrev || null,
            analyte.modus_name ? `(${analyte.modus_name})` : null,
            analyte.name ? `(${analyte.name})` : null
          ]
            .filter(i => i)
            .join(""),
          instrument: i_name,
          result_id: analyte_result ? analyte_result.id : null,
          result: r_name,
          created_by,
          evaluated_by: updated_by,
          status: status
        });
      });
    } else if (this.results.models.length) {
      this.results.each(result => {
        const analyte = result.analyte;
        const i_name = result.instrument_instance
          ? result.instrument_instance.name
          : null;
        const unit = result.unit_of_measure
          ? result.unit_of_measure.abbrev
          : null;
        let r_name = [
          result.formatted_result || null,
          unit || null,
          result.raw_result
            ? `(${result.raw_result}${unit ? unit : null})`
            : null
        ]
          .filter(j => j)
          .join("");
        if (analyte.name === "Completion") {
          r_name = +result.raw_result ? "Completed" : "Not-Complete";
        }
        let created_by = [
          result && result.created_by_user && result.created_by_user.name
            ? result.created_by_user.name
            : null,
          result.created_at
            ? `(${moment(result.created_at).format("ll LT")})`
            : null
        ]
          .filter(l => l)
          .join(" ");
        let updated_by = result.updated_by_user
          ? [
              result.updated_by_user && result.updated_by_user.name
                ? result.updated_by_user.name
                : null,
              result.updated_at
                ? `(${moment(result.updated_at).format("ll LT")})`
                : null
            ]
              .filter(l => l)
              .join(" ")
          : null;
        let status =
          result && result.current_analyte_result_status_log
            ? result.current_analyte_result_status_log.analyte_result_status
                .status
            : null;
        rows.push({
          sample_id: this.sample_id,
          analyte_id: analyte ? analyte.id : null,
          analyte: analyte
            ? [
                analyte.abbrev || null,
                analyte.modus_name ? `(${analyte.modus_name})` : null,
                analyte.name ? `(${analyte.name})` : null
              ]
                .filter(i => !!i)
                .join("")
            : null,
          instrument: i_name,
          result_id: result ? result.id : null,
          result: r_name,
          created_by,
          evaluated_by: updated_by,
          status: status
        });
      });
    }

    this.data = rows;
  }
};
</script>
